import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
	decideMaterialValidation,
	getMaterialValidation,
	requestManualMaterialCorrection,
} from './materialValidationApi';
import {
	getManualAdjustmentState,
	InvalidMaterialValidationContractError,
	isConfirmedManualCorrectionCase,
	isManualCorrectionCase,
	isManualCorrectionReadyForReview,
	isValidMaterialEvaluationSha256,
	MANUAL_INVENTORY_ADJUSTMENT_RESOLUTION_KEY,
	requiresLiveMaterialEvaluation,
	validateMaterialValidationViewModel,
} from './materialValidationContract';

const firstError = error => (Array.isArray(error) ? error[0] : error) || {};

const EVALUATION_CONFLICT_MESSAGES = {
	TechnicianMaterialValidationEvaluationConflict:
		'La evidencia de materiales cambió. Mostramos la evidencia más reciente; revísela antes de aprobar.',
	ManualInventoryAdjustmentEvaluationConflict:
		'La evidencia del ajuste manual cambió. Mostramos la evidencia más reciente; revísela antes de aprobar.',
	OrderInventoryAdjustmentOriginalDrift:
		'El inventario original de la orden cambió. Mostramos la evidencia más reciente; revísela antes de aprobar.',
};

const isMaterialEvaluationConflict = name =>
	Object.prototype.hasOwnProperty.call(EVALUATION_CONFLICT_MESSAGES, name);

export const getMaterialValidationLoadErrorMessage = error => {
	const value = firstError(error);
	if (value.httpStatus === 403)
		return 'No tiene autorización para consultar la validación de materiales.';
	if (value.httpStatus === 404)
		return 'La orden no está disponible dentro de su alcance.';
	return value.message || 'No se pudo confirmar la validación de materiales.';
};

export const getMaterialValidationDecisionErrorMessage = error => {
	const value = firstError(error);
	if (value.name === 'TechnicianMaterialValidationCorrectionBlocked')
		return 'No se puede devolver mientras exista otra validación bloqueante pendiente.';
	if (isMaterialEvaluationConflict(value.name))
		return EVALUATION_CONFLICT_MESSAGES[value.name];
	if (value.httpStatus === 409)
		return 'El estado cambió y no fue posible guardar la decisión. Mostramos el estado más reciente; revíselo antes de intentar nuevamente.';
	if (value.httpStatus === 412)
		return 'La versión del caso cambió. Mostramos el estado más reciente; revíselo antes de intentar nuevamente.';
	if (value.httpStatus === 403)
		return 'No tiene autorización para decidir este caso.';
	if (value.httpStatus === 400)
		return value.message || 'Revise la decisión y el comentario ingresado.';
	return value.message || 'No se pudo guardar la decisión de materiales.';
};

const getManualCorrectionReasonMessages = (error, fallbackReasons = []) => {
	const value = firstError(error);
	const errorReasons = Array.isArray(value.details?.reasons)
		? value.details.reasons
		: [];
	return (errorReasons.length > 0 ? errorReasons : fallbackReasons)
		.map(reason => reason?.message?.trim())
		.filter(Boolean);
};

export const getManualCorrectionRequestErrorMessage = (
	error,
	fallbackReasons = [],
) => {
	const value = firstError(error);
	if (value.name === 'MaterialValidationIdempotencyConflict')
		return 'La solicitud cambió durante el reintento. Revise el estado más reciente antes de intentar nuevamente.';
	if (value.httpStatus === 403)
		return 'No tiene autorización para habilitar esta corrección de materiales.';
	if (value.httpStatus === 404)
		return 'La orden no está disponible dentro de su alcance.';
	if (value.httpStatus === 409) {
		const reasons = getManualCorrectionReasonMessages(error, fallbackReasons);
		if (reasons.length > 0)
			return `No se puede habilitar la corrección de materiales. ${reasons.join(
				' ',
			)}`;
		return (
			value.message ||
			'La solicitud ya no está disponible. Mostramos el estado más reciente.'
		);
	}
	if (value.httpStatus === 400)
		return value.message || 'Revise el motivo y el comentario ingresados.';
	return value.message || 'No se pudo habilitar la corrección de materiales.';
};

const REASON_CODES = {
	APPROVE: 'VALIDATION_APPROVED',
	RETURN_FOR_CORRECTION: 'VALIDATION_REQUIRES_CORRECTION',
};

const MIN_COMMENT_LENGTH = 10;
const MAX_COMMENT_LENGTH = 500;
const REASON_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,99}$/;
const PRICING_STRATEGIES = new Set([
	'PAY_EXPECTED',
	'PAY_REPORTED',
	'PAY_ADJUSTED',
]);

const normalizePricingResolution = (payload, capability) => {
	if (!capability?.required) return undefined;
	const submittedSelections = payload?.pricingResolution?.selections;
	if (!Array.isArray(submittedSelections))
		throw new Error('Seleccione cuánto pagar para cada material discrepante.');
	const byMetricKey = new Map();
	submittedSelections.forEach(selection => {
		const key = String(selection?.metricKey);
		if (!selection || byMetricKey.has(key))
			throw new Error(
				'La resolución de cantidades contiene materiales repetidos.',
			);
		byMetricKey.set(key, selection);
	});

	const normalized = capability.items.map(item => {
		const selection = byMetricKey.get(String(item.metricKey));
		if (
			!selection ||
			!PRICING_STRATEGIES.has(selection.strategy) ||
			!item.strategies.includes(selection.strategy)
		)
			throw new Error(
				'Seleccione cuánto pagar para cada material discrepante.',
			);
		if (selection.strategy !== 'PAY_ADJUSTED')
			return { metricKey: item.metricKey, strategy: selection.strategy };

		const adjustedValue = String(selection.adjustedValue ?? '').trim();
		const justification = String(selection.justification || '').trim();
		const constraints = item.adjustedValueConstraints;
		const decimalPattern = new RegExp(
			constraints.decimalPlaces === 0
				? '^(?:0|[1-9]\\d*)$'
				: `^(?:0|[1-9]\\d*)(?:\\.\\d{1,${constraints.decimalPlaces}})?$`,
		);
		const numericValue = Number(adjustedValue);
		if (
			!decimalPattern.test(adjustedValue) ||
			!Number.isFinite(numericValue) ||
			numericValue < constraints.minValue ||
			(constraints.maxValue !== undefined &&
				constraints.maxValue !== null &&
				numericValue > constraints.maxValue) ||
			justification.length < MIN_COMMENT_LENGTH ||
			justification.length > MAX_COMMENT_LENGTH
		)
			throw new Error(
				'La cantidad ajustada o su justificación no cumple los límites permitidos.',
			);
		const canonicalAdjustedValue = adjustedValue
			.replace(/(\.\d*?[1-9])0+$/, '$1')
			.replace(/\.0+$/, '');
		return {
			metricKey: item.metricKey,
			strategy: selection.strategy,
			adjustedValue: canonicalAdjustedValue,
			justification,
		};
	});
	if (byMetricKey.size !== normalized.length)
		throw new Error(
			'La resolución contiene materiales que ya no están vigentes.',
		);
	return {
		pricingPolicySha256: capability.pricingPolicySha256,
		selections: normalized.sort((left, right) =>
			String(left.metricKey).localeCompare(String(right.metricKey)),
		),
	};
};

const canSubmitDecision = (viewModel, decision) =>
	viewModel?.capabilities?.canDecide === true &&
	viewModel.capabilities.decisions?.includes(decision) &&
	(!isManualCorrectionCase(viewModel?.currentCase) ||
		isManualCorrectionReadyForReview(viewModel));

const MANUAL_ADJUSTMENT_POLL_DELAYS = [5000, 10000, 20000, 30000, 30000];

const sameOrderId = (left, right) =>
	left !== undefined &&
	left !== null &&
	right !== undefined &&
	right !== null &&
	String(left) === String(right);

const findManualCorrectionCase = (viewModel, caseId) =>
	[viewModel?.currentCase, ...(viewModel?.previousCases || [])].find(
		materialCase =>
			isManualCorrectionCase(materialCase) &&
			sameOrderId(materialCase.id, caseId),
	);

const getCurrentManualCorrectionCase = viewModel =>
	viewModel?.currentCase?.resolutionKey ===
	MANUAL_INVENTORY_ADJUSTMENT_RESOLUTION_KEY
		? viewModel.currentCase
		: undefined;

const MANUAL_REFRESH_WARNING =
	'La corrección quedó habilitada, pero no se pudo actualizar el estado. Reintente la actualización.';

export default function useMaterialValidation(
	orderId,
	{ onChanged, isActive = false } = {},
) {
	const [viewModel, setViewModel] = useState();
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState();
	const [contractError, setContractError] = useState();
	const [decisionError, setDecisionError] = useState();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [manualRequestError, setManualRequestError] = useState();
	const [isRequestingManualCorrection, setIsRequestingManualCorrection] =
		useState(false);
	const [manualRefreshWarning, setManualRefreshWarning] = useState();
	const [isRefreshingManualCorrection, setIsRefreshingManualCorrection] =
		useState(false);
	const [isDocumentVisible, setIsDocumentVisible] = useState(
		() =>
			typeof document === 'undefined' || document.visibilityState !== 'hidden',
	);
	const [isPollingManualAdjustment, setIsPollingManualAdjustment] =
		useState(false);
	const [
		manualAdjustmentPollingExhausted,
		setManualAdjustmentPollingExhausted,
	] = useState(false);
	const [manualAdjustmentPollBurst, setManualAdjustmentPollBurst] = useState(0);
	const mountedRef = useRef(false);
	const orderIdRef = useRef(orderId);
	const requestGenerationRef = useRef(0);
	const submissionRef = useRef();
	const retryAttemptRef = useRef();
	const manualSubmissionRef = useRef();
	const manualRetryAttemptRef = useRef();
	const manualConfirmationRef = useRef();
	const manualRefreshAttemptRef = useRef();
	const abortRef = useRef();
	const pollControllerRef = useRef();
	const pollTimerRef = useRef();
	const contractErrorRef = useRef();
	const pollTransientErrorRef = useRef(false);
	const onChangedRef = useRef(onChanged);
	orderIdRef.current = orderId;
	onChangedRef.current = onChanged;

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			requestGenerationRef.current += 1;
			abortRef.current?.abort();
		};
	}, []);

	useEffect(() => {
		if (typeof document === 'undefined') return undefined;
		const onVisibilityChange = () =>
			setIsDocumentVisible(document.visibilityState !== 'hidden');
		document.addEventListener('visibilitychange', onVisibilityChange);
		return () =>
			document.removeEventListener('visibilitychange', onVisibilityChange);
	}, []);

	const load = useCallback(
		({
			preserveCurrent = false,
			background = false,
			pollRequest = false,
		} = {}) => {
			const requestedOrderId = orderId;
			const generation = requestGenerationRef.current + 1;
			requestGenerationRef.current = generation;
			abortRef.current?.abort();
			const controller =
				typeof AbortController === 'undefined'
					? undefined
					: new AbortController();
			abortRef.current = controller;
			if (pollRequest) {
				pollControllerRef.current = controller;
				pollTransientErrorRef.current = false;
			}
			if (!preserveCurrent) setViewModel(undefined);
			else
				setViewModel(current => {
					if (
						!current ||
						!Object.prototype.hasOwnProperty.call(current, 'currentInventory')
					)
						return current;
					const { currentInventory, ...withoutSnapshot } = current;
					return withoutSnapshot;
				});
			if (!background) {
				setIsLoading(true);
				setLoadError(undefined);
				setContractError(undefined);
				contractErrorRef.current = undefined;
			}
			if (!requestedOrderId) {
				if (!background) setIsLoading(false);
				return Promise.resolve(undefined);
			}

			return getMaterialValidation(requestedOrderId, {
				controlSignal: controller?.signal,
			})
				.then(response => {
					if (
						!mountedRef.current ||
						orderIdRef.current !== requestedOrderId ||
						requestGenerationRef.current !== generation
					)
						return undefined;
					const validated = validateMaterialValidationViewModel(response);
					if (!sameOrderId(validated.orderId, requestedOrderId))
						throw new InvalidMaterialValidationContractError(
							'La respuesta pertenece a otra orden.',
						);
					setViewModel(validated);
					setLoadError(undefined);
					setContractError(undefined);
					contractErrorRef.current = undefined;
					return validated;
				})
				.catch(error => {
					if (
						!mountedRef.current ||
						orderIdRef.current !== requestedOrderId ||
						requestGenerationRef.current !== generation
					)
						return undefined;
					if (error instanceof InvalidMaterialValidationContractError) {
						const message =
							'La respuesta de validación de materiales no cumple el contrato esperado.';
						contractErrorRef.current = message;
						setContractError(message);
					} else if (!background) {
						setLoadError(getMaterialValidationLoadErrorMessage(error));
					} else if (pollRequest) {
						pollTransientErrorRef.current = true;
					}
					return undefined;
				})
				.finally(() => {
					if (pollRequest && pollControllerRef.current === controller)
						pollControllerRef.current = undefined;
					if (
						!background &&
						mountedRef.current &&
						orderIdRef.current === requestedOrderId &&
						requestGenerationRef.current === generation
					)
						setIsLoading(false);
				});
		},
		[orderId],
	);

	useEffect(() => {
		retryAttemptRef.current = undefined;
		submissionRef.current = undefined;
		manualRetryAttemptRef.current = undefined;
		manualSubmissionRef.current = undefined;
		manualConfirmationRef.current = undefined;
		manualRefreshAttemptRef.current = undefined;
		setDecisionError(undefined);
		setIsSubmitting(false);
		setManualRequestError(undefined);
		setIsRequestingManualCorrection(false);
		setManualRefreshWarning(undefined);
		setIsRefreshingManualCorrection(false);
		load();
		return () => {
			requestGenerationRef.current += 1;
			abortRef.current?.abort();
		};
	}, [load]);

	const refresh = useCallback(() => load({ preserveCurrent: true }), [load]);

	const manualAdjustmentState = getManualAdjustmentState(viewModel);
	const manualAdjustmentCaseId = isManualCorrectionCase(viewModel?.currentCase)
		? viewModel.currentCase.id
		: undefined;
	const manualAdjustmentCaseVersion = isManualCorrectionCase(
		viewModel?.currentCase,
	)
		? viewModel.currentCase.version
		: undefined;

	useEffect(() => {
		clearTimeout(pollTimerRef.current);
		if (pollControllerRef.current) {
			requestGenerationRef.current += 1;
			pollControllerRef.current.abort();
		}
		pollControllerRef.current = undefined;
		setIsPollingManualAdjustment(false);

		const canPoll =
			isActive &&
			isDocumentVisible &&
			manualAdjustmentState === 'EDITING' &&
			!contractError;
		if (!canPoll) {
			if (manualAdjustmentState !== 'EDITING')
				setManualAdjustmentPollingExhausted(false);
			return undefined;
		}

		let cancelled = false;
		let attempt = 0;
		setManualAdjustmentPollingExhausted(false);
		const schedule = () => {
			if (cancelled) return;
			if (attempt >= MANUAL_ADJUSTMENT_POLL_DELAYS.length) {
				setManualAdjustmentPollingExhausted(true);
				return;
			}
			pollTimerRef.current = setTimeout(async () => {
				if (cancelled) return;
				setIsPollingManualAdjustment(true);
				const validated = await load({
					preserveCurrent: true,
					background: true,
					pollRequest: true,
				});
				if (cancelled) return;
				setIsPollingManualAdjustment(false);
				attempt += 1;
				if (
					contractErrorRef.current ||
					(validated && getManualAdjustmentState(validated) !== 'EDITING') ||
					(!validated && !pollTransientErrorRef.current)
				)
					return;
				schedule();
			}, MANUAL_ADJUSTMENT_POLL_DELAYS[attempt]);
		};
		schedule();

		return () => {
			cancelled = true;
			clearTimeout(pollTimerRef.current);
			if (pollControllerRef.current) {
				requestGenerationRef.current += 1;
				pollControllerRef.current.abort();
			}
			pollControllerRef.current = undefined;
		};
	}, [
		contractError,
		isActive,
		isDocumentVisible,
		load,
		manualAdjustmentCaseId,
		manualAdjustmentCaseVersion,
		manualAdjustmentPollBurst,
		manualAdjustmentState,
	]);

	const refreshManualAdjustmentState = useCallback(async () => {
		setManualAdjustmentPollingExhausted(false);
		setIsPollingManualAdjustment(true);
		const validated = await load({
			preserveCurrent: true,
			background: true,
			pollRequest: true,
		});
		if (mountedRef.current) {
			setIsPollingManualAdjustment(false);
			if (
				!contractErrorRef.current &&
				(getManualAdjustmentState(validated) === 'EDITING' ||
					(!validated && pollTransientErrorRef.current))
			)
				setManualAdjustmentPollBurst(current => current + 1);
		}
		return validated;
	}, [load]);

	const refreshConfirmedManualCorrection = useCallback(confirmation => {
		const expected = confirmation || manualConfirmationRef.current;
		if (
			!expected ||
			!mountedRef.current ||
			!sameOrderId(orderIdRef.current, expected.orderId)
		)
			return Promise.resolve(undefined);

		const generation = requestGenerationRef.current + 1;
		requestGenerationRef.current = generation;
		abortRef.current?.abort();
		const controller =
			typeof AbortController === 'undefined'
				? undefined
				: new AbortController();
		const refreshAttempt = { controller, generation };
		manualRefreshAttemptRef.current = refreshAttempt;
		abortRef.current = controller;
		setManualRefreshWarning(undefined);
		setIsRefreshingManualCorrection(true);

		return getMaterialValidation(expected.orderId, {
			controlSignal: controller?.signal,
		})
			.then(response => {
				if (
					!mountedRef.current ||
					!sameOrderId(orderIdRef.current, expected.orderId) ||
					requestGenerationRef.current !== generation
				)
					return undefined;
				const validated = validateMaterialValidationViewModel(response);
				const confirmedCase = findManualCorrectionCase(
					validated,
					expected.caseId,
				);
				if (
					!sameOrderId(validated.orderId, expected.orderId) ||
					!confirmedCase ||
					!Number.isSafeInteger(confirmedCase.version) ||
					confirmedCase.version < expected.version ||
					(confirmedCase.status === 'PENDING_REVIEW' &&
						!isConfirmedManualCorrectionCase(confirmedCase) &&
						!isManualCorrectionReadyForReview(validated))
				) {
					setManualRefreshWarning(MANUAL_REFRESH_WARNING);
					return undefined;
				}
				setViewModel(validated);
				manualConfirmationRef.current = {
					...expected,
					version: confirmedCase.version,
				};
				setLoadError(undefined);
				setContractError(undefined);
				setManualRefreshWarning(undefined);
				return validated;
			})
			.catch(() => {
				if (
					!mountedRef.current ||
					!sameOrderId(orderIdRef.current, expected.orderId) ||
					requestGenerationRef.current !== generation
				)
					return undefined;
				setManualRefreshWarning(MANUAL_REFRESH_WARNING);
				return undefined;
			})
			.finally(() => {
				if (manualRefreshAttemptRef.current === refreshAttempt) {
					manualRefreshAttemptRef.current = undefined;
					if (
						mountedRef.current &&
						sameOrderId(orderIdRef.current, expected.orderId)
					)
						setIsRefreshingManualCorrection(false);
				}
			});
	}, []);

	const retryManualCorrectionRefresh = useCallback(
		() => refreshConfirmedManualCorrection(),
		[refreshConfirmedManualCorrection],
	);

	const decide = useCallback(
		payload => {
			if (submissionRef.current) return submissionRef.current;
			const submittedOrderId = orderId;
			const submittedViewModel = viewModel;
			const submittedCase = submittedViewModel?.currentCase;
			if (
				payload?.decision === 'APPROVE' &&
				isManualCorrectionCase(submittedCase) &&
				!isManualCorrectionReadyForReview(submittedViewModel)
			)
				return Promise.reject(
					new Error(
						'La evidencia vigente no está disponible. Actualice la validación antes de aprobar.',
					),
				);
			const canDecide = canSubmitDecision(
				submittedViewModel,
				payload?.decision,
			);
			if (
				!submittedCase ||
				!sameOrderId(submittedViewModel?.orderId, submittedOrderId) ||
				submittedViewModel?.consistency?.state !== 'CONSISTENT' ||
				!canDecide
			)
				return Promise.reject(new Error('La decisión no está disponible.'));
			const requiresEvaluation =
				requiresLiveMaterialEvaluation(
					submittedCase,
					submittedViewModel?.currentEvaluation,
				) ||
				(payload?.decision === 'APPROVE' &&
					(isManualCorrectionCase(submittedCase) ||
						submittedViewModel?.capabilities?.approvalQuantitySelection
							?.required === true));
			const expectedEvaluationSha256 =
				submittedViewModel?.currentEvaluation?.contentSha256 ||
				submittedCase?.input?.contentSha256;
			if (
				payload?.decision === 'APPROVE' &&
				requiresEvaluation &&
				!isValidMaterialEvaluationSha256(expectedEvaluationSha256)
			)
				return Promise.reject(
					new Error(
						'La evidencia vigente no está disponible. Actualice la validación antes de aprobar.',
					),
				);

			const comment = payload?.comment?.trim() || '';
			if (
				comment.length < MIN_COMMENT_LENGTH ||
				comment.length > MAX_COMMENT_LENGTH
			)
				return Promise.reject(
					new Error(
						`El comentario debe tener entre ${MIN_COMMENT_LENGTH} y ${MAX_COMMENT_LENGTH} caracteres.`,
					),
				);
			let normalizedPricingResolution;
			try {
				normalizedPricingResolution =
					payload.decision === 'APPROVE'
						? normalizePricingResolution(
								payload,
								submittedViewModel.capabilities.approvalQuantitySelection,
							)
						: undefined;
			} catch (error) {
				return Promise.reject(error);
			}
			const normalizedPayload = {
				decision: payload.decision,
				reasonCode: REASON_CODES[payload.decision],
				comment,
				...(payload.decision === 'APPROVE' && requiresEvaluation
					? { expectedEvaluationSha256 }
					: {}),
				...(normalizedPricingResolution
					? { pricingResolution: normalizedPricingResolution }
					: {}),
			};
			const signature = JSON.stringify([
				submittedOrderId,
				submittedCase.id,
				submittedCase.version,
				normalizedPayload.decision,
				normalizedPayload.reasonCode,
				normalizedPayload.comment,
				normalizedPayload.expectedEvaluationSha256 || null,
				normalizedPayload.pricingResolution || null,
				submittedViewModel.capabilities.expectedVersion,
			]);
			const idempotencyKey =
				retryAttemptRef.current?.signature === signature
					? retryAttemptRef.current.idempotencyKey
					: uuidv4();
			retryAttemptRef.current = { signature, idempotencyKey };
			const decisionGeneration = requestGenerationRef.current;
			setDecisionError(undefined);
			setIsSubmitting(true);

			const promise = decideMaterialValidation(
				submittedOrderId,
				submittedCase.id,
				normalizedPayload,
				{
					version: submittedViewModel.capabilities.expectedVersion,
					idempotencyKey,
				},
			)
				.then(response => {
					if (
						!mountedRef.current ||
						orderIdRef.current !== submittedOrderId ||
						requestGenerationRef.current !== decisionGeneration
					)
						return undefined;
					const validated = validateMaterialValidationViewModel(response);
					if (!sameOrderId(validated.orderId, submittedOrderId))
						throw new InvalidMaterialValidationContractError(
							'La respuesta pertenece a otra orden.',
						);
					setViewModel(validated);
					setContractError(undefined);
					retryAttemptRef.current = undefined;
					if (onChangedRef.current) onChangedRef.current(validated);
					return validated;
				})
				.catch(async error => {
					if (
						!mountedRef.current ||
						orderIdRef.current !== submittedOrderId ||
						requestGenerationRef.current !== decisionGeneration
					)
						return undefined;
					if (error instanceof InvalidMaterialValidationContractError) {
						retryAttemptRef.current = undefined;
						const refreshPromise = load({ preserveCurrent: true });
						const refreshGeneration = requestGenerationRef.current;
						await refreshPromise;
						if (
							!mountedRef.current ||
							orderIdRef.current !== submittedOrderId ||
							requestGenerationRef.current !== refreshGeneration
						)
							throw error;
						const message =
							'La respuesta de la decisión no cumple el contrato esperado.';
						setDecisionError(message);
						throw error;
					}
					const decisionFailure = firstError(error);
					const status = decisionFailure.httpStatus;
					if (status === 409 || status === 412) {
						retryAttemptRef.current = undefined;
						setViewModel(undefined);
						const refreshPromise = load({ preserveCurrent: false });
						const refreshGeneration = requestGenerationRef.current;
						await refreshPromise;
						if (
							!mountedRef.current ||
							orderIdRef.current !== submittedOrderId ||
							requestGenerationRef.current !== refreshGeneration
						)
							throw error;
					}
					setDecisionError(getMaterialValidationDecisionErrorMessage(error));
					throw error;
				})
				.finally(() => {
					if (submissionRef.current === promise)
						submissionRef.current = undefined;
					if (mountedRef.current && orderIdRef.current === submittedOrderId)
						setIsSubmitting(false);
				});
			submissionRef.current = promise;
			return promise;
		},
		[load, orderId, viewModel],
	);

	const requestManualCorrection = useCallback(
		payload => {
			if (manualSubmissionRef.current) return manualSubmissionRef.current;
			const submittedOrderId = orderId;
			const submittedViewModel = viewModel;
			const manualCapability =
				submittedViewModel?.capabilities?.manualCorrectionRequest;
			if (
				!sameOrderId(submittedViewModel?.orderId, submittedOrderId) ||
				manualCapability?.allowed !== true ||
				manualCapability.mode !== 'INVENTORY_ADJUSTMENT'
			)
				return Promise.reject(
					new Error('La solicitud de corrección manual no está disponible.'),
				);

			const reasonCode = payload?.reasonCode?.trim() || '';
			const comment = payload?.comment?.trim() || '';
			if (!REASON_CODE_PATTERN.test(reasonCode))
				return Promise.reject(
					new Error('Seleccione un motivo válido para la corrección.'),
				);
			if (
				comment.length < MIN_COMMENT_LENGTH ||
				comment.length > MAX_COMMENT_LENGTH
			)
				return Promise.reject(
					new Error(
						`El comentario debe tener entre ${MIN_COMMENT_LENGTH} y ${MAX_COMMENT_LENGTH} caracteres.`,
					),
				);

			const normalizedPayload = { reasonCode, comment };
			const signature = JSON.stringify([
				submittedOrderId,
				normalizedPayload.reasonCode,
				normalizedPayload.comment,
			]);
			const idempotencyKey =
				manualRetryAttemptRef.current?.signature === signature
					? manualRetryAttemptRef.current.idempotencyKey
					: uuidv4();
			manualRetryAttemptRef.current = { signature, idempotencyKey };
			setManualRequestError(undefined);
			setIsRequestingManualCorrection(true);

			const promise = requestManualMaterialCorrection(
				submittedOrderId,
				normalizedPayload,
				{ idempotencyKey },
			)
				.then(response => {
					if (
						!mountedRef.current ||
						!sameOrderId(orderIdRef.current, submittedOrderId)
					)
						return undefined;
					const validated = validateMaterialValidationViewModel(response);
					const confirmedCase = getCurrentManualCorrectionCase(validated);
					if (
						!sameOrderId(validated.orderId, submittedOrderId) ||
						!isConfirmedManualCorrectionCase(confirmedCase)
					)
						throw new InvalidMaterialValidationContractError(
							'La respuesta no identifica la corrección manual creada.',
						);

					requestGenerationRef.current += 1;
					abortRef.current?.abort();
					const confirmation = {
						orderId: submittedOrderId,
						caseId: confirmedCase.id,
						version: confirmedCase.version,
					};
					manualConfirmationRef.current = confirmation;
					setViewModel(validated);
					setLoadError(undefined);
					setContractError(undefined);
					setManualRequestError(undefined);
					setManualRefreshWarning(undefined);
					manualRetryAttemptRef.current = undefined;
					if (onChangedRef.current) onChangedRef.current(validated);
					refreshConfirmedManualCorrection(confirmation);
					return validated;
				})
				.catch(async error => {
					if (
						!mountedRef.current ||
						!sameOrderId(orderIdRef.current, submittedOrderId)
					)
						return undefined;

					if (error instanceof InvalidMaterialValidationContractError) {
						const refreshPromise = load({ preserveCurrent: true });
						const refreshGeneration = requestGenerationRef.current;
						await refreshPromise;
						if (
							!mountedRef.current ||
							!sameOrderId(orderIdRef.current, submittedOrderId) ||
							requestGenerationRef.current !== refreshGeneration
						)
							throw error;
						setManualRequestError(
							'La respuesta de la solicitud no cumple el contrato esperado.',
						);
						throw error;
					}

					const requestFailure = firstError(error);
					let fallbackReasons = [];
					if (requestFailure.httpStatus === 409) {
						manualRetryAttemptRef.current = undefined;
						const refreshPromise = load({ preserveCurrent: true });
						const refreshGeneration = requestGenerationRef.current;
						const refreshed = await refreshPromise;
						if (
							!mountedRef.current ||
							!sameOrderId(orderIdRef.current, submittedOrderId) ||
							requestGenerationRef.current !== refreshGeneration
						)
							throw error;
						fallbackReasons =
							refreshed?.capabilities?.manualCorrectionRequest?.reasons || [];
					}
					setManualRequestError(
						getManualCorrectionRequestErrorMessage(error, fallbackReasons),
					);
					throw error;
				})
				.finally(() => {
					if (manualSubmissionRef.current === promise)
						manualSubmissionRef.current = undefined;
					if (
						mountedRef.current &&
						sameOrderId(orderIdRef.current, submittedOrderId)
					)
						setIsRequestingManualCorrection(false);
				});
			manualSubmissionRef.current = promise;
			return promise;
		},
		[load, orderId, refreshConfirmedManualCorrection, viewModel],
	);

	return {
		viewModel: sameOrderId(viewModel?.orderId, orderId) ? viewModel : undefined,
		isLoading,
		loadError,
		contractError,
		decisionError,
		isSubmitting,
		manualRequestError,
		isRequestingManualCorrection,
		manualRefreshWarning,
		isRefreshingManualCorrection,
		isPollingManualAdjustment,
		manualAdjustmentPollingExhausted,
		refresh,
		refreshManualAdjustmentState,
		decide,
		requestManualCorrection,
		retryManualCorrectionRefresh,
	};
}
